import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useWebSocket } from '../components/WebSocketContext';
import {
  useActionAIModels,
  useActionScope,
  useActionSettingsCommand,
  useActionSettingsPayload,
} from '../components/Actions/ActionScopeContext';
import { isActionEnabled } from '../Utils/actionSettingsUtils';
import { buildForceRunPayload, normalizeForceRunSelection } from '../Utils/forceRunUtils';
import {
  buildActionAIModelData,
  normalizeActionAIModelSelections,
} from '../Utils/actionAIModelUtils';

const normalizeConditionLogic = (value) => {
  const normalized = String(value || 'AND').toUpperCase();
  return normalized === 'OR' ? 'OR' : 'AND';
};

const buildConditionsState = (conditions = [], conditionLogic = 'AND') => {
  const nextConditions = Array.isArray(conditions) ? [...conditions] : [];
  Object.defineProperty(nextConditions, 'conditionLogic', {
    value: normalizeConditionLogic(conditionLogic),
    enumerable: false,
    configurable: true,
  });
  return nextConditions;
};

const parseCustomData = (customData) => {
  if (!customData) {
    return {};
  }
  if (typeof customData === 'string') {
    try {
      const parsed = JSON.parse(customData);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (error) {
      console.error('Failed to parse action customData:', error);
      return {};
    }
  }
  return typeof customData === 'object' && !Array.isArray(customData) ? customData : {};
};

const normalizeActionSettingsPayload = (payload = {}) => {
  if (!payload || typeof payload !== 'object') {
    return { customData: {} };
  }

  const customData = parseCustomData(payload.customData ?? payload.data);
  return {
    ...payload,
    customData,
  };
};

/**
 * Unified hook for managing action settings across all action components
 * Handles common functionality like enable/disable, conditions, save/force run, button states
 */
export const useActionSettings = ({
  settingId,
  caseid,
  actionType,
  onSaveStatusChange,
  customComparison = () => true, // Custom function to compare additional fields
  customSaveData = () => ({}), // Custom function to generate additional save data
  customForceRunData = null, // Optional custom data override for force run without saving
  loadDataFunction = null, // Custom function to load data
  enableConditions = true,
  initialScope = 'event' // Initial scope for new actions
}) => {
  const currentScopeFromContext = useActionScope() || initialScope || 'event';
  const initialSettingsPayload = useActionSettingsPayload();
  const sendActionCommand = useActionSettingsCommand();
  const { actionAIModels, setActionAIModels, isPluginAction } = useActionAIModels();
  const actionAIModelData = useMemo(
    () => buildActionAIModelData(actionType, actionAIModels, isPluginAction),
    [actionType, actionAIModels, isPluginAction]
  );
  // Common state
  const [isEnabled, setIsEnabled] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [conditionLogic, setConditionLogic] = useState('AND');
  const [conditions, setConditions] = useState(() => buildConditionsState([], 'AND'));
  const [id, setId] = useState(settingId || 0);
  const [forceRunLoading, setForceRunLoading] = useState(false);
  const [forceRunResult, setForceRunResult] = useState(null);
  const [popUpOpen, setPopUpOpen] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [loadedSettingsPayload, setLoadedSettingsPayload] = useState(null);
  const forceRunWarningOpen = false;
  const forceRunWarningCount = 0;
  
  const initialSettings = useRef({});
  const customForceRunDataRef = useRef(customForceRunData);
  const appliedLoadKeyRef = useRef('');
  const appliedInitialPayloadKeyRef = useRef('');
  const { ws, subscribeToMessages, sendMessage } = useWebSocket();

  useLayoutEffect(() => {
    customForceRunDataRef.current = customForceRunData;
  }, [customForceRunData]);

  const getCustomForceRunData = useCallback(() => {
    const forceRunDataBuilder = customForceRunDataRef.current;
    if (typeof forceRunDataBuilder !== 'function') {
      return null;
    }

    try {
      const forceRunData = forceRunDataBuilder() || {};
      JSON.stringify(forceRunData);
      return forceRunData;
    } catch (error) {
      console.error('Error getting or serializing custom force run data:', error);
      return null;
    }
  }, []);

  const applyLoadedSettings = useCallback((normalizedSettings) => {
    const { id: newId, enable, customData, scope } = normalizedSettings;
    setId(newId);
    setIsEnabled(isActionEnabled(enable));
    setActionAIModels(normalizeActionAIModelSelections(customData));
    const loadedConditionLogic = normalizeConditionLogic(customData?.conditionLogic);
    setConditionLogic(loadedConditionLogic);

    if (enableConditions && customData?.conditions !== undefined) {
      setConditions(buildConditionsState(customData.conditions, loadedConditionLogic));
    } else {
      setConditions(buildConditionsState([], loadedConditionLogic));
    }

    initialSettings.current = {
      id: newId,
      enable: isActionEnabled(enable),
      useConditions: enableConditions,
      conditions: customData?.conditions || [],
      ...customData,
      conditionLogic: loadedConditionLogic,
      scope: scope || currentScopeFromContext || initialScope || 'event',
    };

    setLoadedSettingsPayload(normalizedSettings);
    setSettingsLoaded(true);
    setIsSaveDisabled(true);
  }, [currentScopeFromContext, enableConditions, initialScope, setActionAIModels]);

  // WebSocket/HTTP bridge message handler.
  useEffect(() => {
    const unsubscribe = subscribeToMessages(['getExportSettings', 'forceRun', 'saveActionSettings'], (parsedMessage) => {
      try {
        if (parsedMessage.command === 'getExportSettings') {
          const normalizedSettings = normalizeActionSettingsPayload(parsedMessage.data);
          const messageMatchesAction = normalizedSettings.type === actionType
            || String(normalizedSettings.id || '') === String(settingId || id || '');

          if (!messageMatchesAction) {
            return;
          }

          console.log('Received export settings:', normalizedSettings);
          applyLoadedSettings(normalizedSettings);
        } else if (parsedMessage.command === 'forceRun') {
          if (parsedMessage.data && parsedMessage.data.type === actionType) {
            setForceRunResult({
              status: parsedMessage.data.status,
              persistent: parsedMessage.data.persistent === true,
              total: parsedMessage.data.total,
              success: parsedMessage.data.success || 0,
              skipped: parsedMessage.data.skipped || 0,
              fail: parsedMessage.data.fail || 0,
              files_failed: parsedMessage.data.files_failed,
              failure_details: parsedMessage.data.failure_details || [],
              output_tag_results: parsedMessage.data.output_tag_results || [],
              output_tag_results_truncated: parsedMessage.data.output_tag_results_truncated || 0
            });
            setForceRunLoading(false);
            setPopUpOpen(true);
          }
        } else if (parsedMessage.command === 'saveActionSettings') {
          if (parsedMessage.data && parsedMessage.data.id && parsedMessage.data.type === actionType) {
            const savedId = parsedMessage.data.id;
            const savedCustomData = parsedMessage.data.customData;
            const normalizedSavedCustomData = parseCustomData(savedCustomData);
            
            setId(savedId);
            setActionAIModels(normalizeActionAIModelSelections(normalizedSavedCustomData));
            initialSettings.current = {
              id: savedId,
              enable: isEnabled,
              useConditions: enableConditions,
              conditions: [...conditions],
              conditionLogic,
              scope: parsedMessage.data.scope || currentScopeFromContext || initialScope || 'event',
              data: normalizedSavedCustomData,
              ...normalizedSavedCustomData,
              ...buildActionAIModelData(actionType, normalizedSavedCustomData, isPluginAction),
              ...customSaveData(savedCustomData)
            };
            
            // Immediately disable save button and enable run button
            setIsSaveDisabled(true);
            
            if (onSaveStatusChange) {
              onSaveStatusChange(savedId, false, typeof id === 'string' && id.startsWith('temp_') ? id : null);
            }
          }
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    });
    return unsubscribe;
  }, [subscribeToMessages, actionType, settingId, id, applyLoadedSettings, isEnabled, enableConditions, conditions, conditionLogic, currentScopeFromContext, initialScope, customSaveData, onSaveStatusChange, isPluginAction, setActionAIModels]);

  useEffect(() => {
    if (!loadDataFunction || !loadedSettingsPayload) {
      return;
    }

    const loadKey = `${loadedSettingsPayload.id || ''}:${loadedSettingsPayload.type || ''}:${JSON.stringify(loadedSettingsPayload.customData || {})}`;
    if (appliedLoadKeyRef.current === loadKey) {
      return;
    }

    appliedLoadKeyRef.current = loadKey;
    try {
      loadDataFunction(loadedSettingsPayload, loadedSettingsPayload.customData || {});
    } catch (error) {
      console.error('Error loading action-specific settings:', error);
    }
  }, [loadedSettingsPayload, loadDataFunction]);

  // Reset local load guards when switching to another action.
  useEffect(() => {
    const isTemporaryId = typeof settingId === 'string' && settingId.startsWith('temp_');
    setSettingsLoaded(false);
    setLoadedSettingsPayload(null);
    appliedLoadKeyRef.current = '';
    appliedInitialPayloadKeyRef.current = '';

    if (isTemporaryId) {
      setId(settingId);
      setIsEnabled(false);
      setConditionLogic('AND');
      setConditions(buildConditionsState([], 'AND'));
      setActionAIModels(normalizeActionAIModelSelections());

      const defaultData = {
        name: `New ${actionType.charAt(0).toUpperCase() + actionType.slice(1)} Flow`
      };

      initialSettings.current = {
        id: settingId,
        enable: false,
        useConditions: enableConditions,
        conditions: [],
        conditionLogic: 'AND',
        scope: currentScopeFromContext || initialScope || 'event',
        data: defaultData
      };
      setSettingsLoaded(true);
      setIsSaveDisabled(false);
    }
  }, [settingId, actionType, enableConditions, currentScopeFromContext, initialScope, setActionAIModels]);

  // Hydrate immediately from the setting already available in the dialog.
  useEffect(() => {
    const isTemporaryId = typeof settingId === 'string' && settingId.startsWith('temp_');
    if (!settingId || isTemporaryId || !initialSettingsPayload) {
      return;
    }
    if (String(initialSettingsPayload.id || '') !== String(settingId)) {
      return;
    }

    const normalizedPayload = normalizeActionSettingsPayload(initialSettingsPayload);
    const payloadKey = `${normalizedPayload.id || ''}:${normalizedPayload.type || ''}:${JSON.stringify(normalizedPayload.customData || {})}`;
    if (appliedInitialPayloadKeyRef.current === payloadKey) {
      return;
    }

    appliedInitialPayloadKeyRef.current = payloadKey;
    applyLoadedSettings(normalizedPayload);
  }, [settingId, initialSettingsPayload, applyLoadedSettings]);

  // Update save disabled state
  const updateSaveDisabledState = useCallback(() => {
    if (initialSettings.current && initialSettings.current.id !== undefined) {
      const initialConditionsJSON = JSON.stringify(initialSettings.current.conditions || []);
      const currentConditionsJSON = JSON.stringify(conditions);
      const initialConditionLogic = normalizeConditionLogic(initialSettings.current.conditionLogic);

      const commonUnchanged = 
        isEnabled === initialSettings.current.enable &&
        (!enableConditions || (
          initialSettings.current.useConditions === enableConditions &&
          currentConditionsJSON === initialConditionsJSON &&
          conditionLogic === initialConditionLogic
        ));

      const customUnchanged = customComparison(initialSettings.current);
      const initialActionAIModelData = buildActionAIModelData(
        actionType,
        initialSettings.current,
        isPluginAction
      );
      const actionAIModelsUnchanged = Object.keys(actionAIModelData).every(
        (fieldName) => String(initialActionAIModelData[fieldName] || '') === String(actionAIModelData[fieldName] || '')
      );
      const unchanged = commonUnchanged && actionAIModelsUnchanged && customUnchanged;

      const hasChanges = !unchanged;
      const currentId = (typeof id === 'string' && id.startsWith('temp_')) ? id : (initialSettings.current.id || id);

      if (onSaveStatusChange) {
        onSaveStatusChange(currentId, hasChanges);
      }
      setIsSaveDisabled(unchanged);
    }
  }, [isEnabled, conditions, conditionLogic, id, onSaveStatusChange, customComparison, enableConditions, actionType, actionAIModelData, isPluginAction]);

  useEffect(() => {
    updateSaveDisabledState();
  }, [isEnabled, conditions, conditionLogic, actionAIModelData, updateSaveDisabledState]);

  // Handle conditions change
  const handleConditionsChange = useCallback((newConditions) => {
    if (newConditions && !Array.isArray(newConditions) && Array.isArray(newConditions.conditions)) {
      const nextConditionLogic = normalizeConditionLogic(newConditions.conditionLogic);
      setConditionLogic(nextConditionLogic);
      setConditions(buildConditionsState(newConditions.conditions, nextConditionLogic));
      return;
    }
    setConditions(buildConditionsState(newConditions, conditionLogic));
  }, [conditionLogic]);

  // Handle save
  const handleSave = useCallback(() => {
    const scopeToPersist = currentScopeFromContext || initialScope || 'event';
    const currentId = (typeof id === 'string' && id.startsWith('temp_')) ? id : (initialSettings.current.id || id);

    let customData = {};
    try {
      customData = customSaveData() || {};
      JSON.stringify(customData);
    } catch (error) {
      console.error('Error getting or serializing custom save data:', error);
      customData = {};
    }

    const baseSettingsData = {
      ...(enableConditions && {
        useConditions: true,
        conditions,
        conditionLogic
      }),
      ...customData,
      ...actionAIModelData,
    };

    try {
      const savePayload = {
        command: 'saveActionSettings',
        id: currentId,
        caseid,
        type: actionType,
        enable: isEnabled ? '1' : '0',
        interval: '0',
        data: baseSettingsData,
        scope: scopeToPersist
      };

      if (typeof sendActionCommand === 'function') {
        sendActionCommand(savePayload);
      } else {
        sendMessage(savePayload);
      }
    } catch (error) {
      console.error('Error sending save action settings:', error);
      throw error;
    }
  }, [sendActionCommand, sendMessage, isEnabled, conditions, conditionLogic, caseid, actionType, id, enableConditions, customSaveData, currentScopeFromContext, initialScope, actionAIModelData]);

  // Handle force run
  const handleForceRun = useCallback((mode = 'all_events', selectedTimeeventId = null, persistent = false) => {
    if (!ws) {
      return;
    }
    const selection = normalizeForceRunSelection(mode);

    try {
      const payload = buildForceRunPayload({
        caseid,
        exportId: id,
        selection,
        timeeventId: selectedTimeeventId,
        customData: getCustomForceRunData(),
        persistent,
      });

      setForceRunLoading(true);
      ws.send(JSON.stringify(payload));
    } catch (error) {
      console.error('Error building force run payload:', error);
      setForceRunLoading(false);
    }
  }, [ws, caseid, id, getCustomForceRunData]);

  const handleForceRunConfirm = useCallback((mode, selectedTimeeventId = null, persistent = false) => {
    handleForceRun(mode, selectedTimeeventId, persistent);
  }, [handleForceRun]);

  const handleForceRunCancel = useCallback(() => {
    setForceRunLoading(false);
  }, []);

  // Close status window
  const handleCloseStatusWindow = useCallback(() => {
    setPopUpOpen(false);
    setForceRunResult(null);
  }, []);

  return {
    // State
    isEnabled,
    setIsEnabled,
    isSaveDisabled,
    conditions,
    conditionLogic,
    id,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    settingsLoaded,
    loadedSettings: loadedSettingsPayload,
    loadedCustomData: loadedSettingsPayload?.customData || {},
    initialSettings,

    // Handlers
    handleConditionsChange,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
    updateSaveDisabledState
  };
};

export default useActionSettings;

